import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that adds the cart drawer's recommended add-on to the cart.
 *
 * Posts a single unit to the cart AJAX API and requests the cart sections back, then
 * dispatches a `CartAddEvent` so `cart-items-component` morphs the drawer in place.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLButtonElement} addButton - The add to cart button.
 * @property {HTMLElement} errorContainer - The error message wrapper.
 * @property {HTMLElement} errorText - The error message text.
 *
 * @extends {Component<CartUpsellRefs>}
 */
class CartUpsell extends Component {
  requiredRefs = ['addButton', 'errorContainer', 'errorText'];

  /** @type {boolean} */
  #pending = false;

  /**
   * Adds one unit of the upsell variant to the cart.
   * @param {MouseEvent} event - The click event.
   */
  addToCart = async (event) => {
    const { variantId } = this.dataset;
    if (this.#pending || !variantId) return;

    this.#setPending(true);
    this.#hideError();

    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: Number(variantId), quantity: 1 }] };

    const sectionIds = this.#cartSectionIds();
    if (sectionIds) body.sections = sectionIds;

    try {
      const config = fetchConfig('json', { body: JSON.stringify(body) });
      const response = await fetch(Theme.routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'cart-upsell-component',
          itemCount: 1,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError();
    } finally {
      this.#setPending(false);
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  };

  /**
   * The section ids the cart renders into, so the add response comes back with fresh markup.
   * @returns {string} A comma separated list of section ids.
   */
  #cartSectionIds() {
    /** @type {Set<string>} */
    const ids = new Set();

    for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }

    return Array.from(ids).join(',');
  }

  /**
   * Locks the button while a request is in flight so repeat clicks can't add duplicates.
   * @param {boolean} pending - Whether a request is in flight.
   */
  #setPending(pending) {
    this.#pending = pending;

    const { addButton } = this.refs;
    if (!addButton) return;

    addButton.disabled = pending;
    addButton.setAttribute('aria-busy', String(pending));
  }

  /**
   * @param {string} [message] - The message from the cart API, if any.
   */
  #showError(message) {
    const { errorContainer, errorText } = this.refs;
    if (!errorContainer || !errorText) return;

    errorText.textContent = message || this.dataset.errorText || '';
    errorContainer.classList.remove('hidden');
  }

  #hideError() {
    this.refs.errorContainer?.classList.add('hidden');
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsell);
}
